import {
    getAccountApplication,
    listAccountApplications
} from './queries';
import {
    createAccountApplication,
    deleteAccountApplication,
    updateAccountApplication,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';

import {
    AccountApplication,
    AccountApplicationStatus,
    CreateAccountApplication,
    DeleteAccountApplication,
    GetAccountApplication,
    ListAccountApplications,
    UpdateAccountApplication
} from "../types.ts";
import {
    notifyError,
    notifySuccess,
    showErrorNotification
} from "../../../../components/PlatformNotification";
import {generateClient} from "aws-amplify/api";

// import {
//     notifyError,
//     notifySuccess
// } from "@flexinet/ui-components";


const client = generateClient();

const normalizeFilter = (filter: { [key: string]: string }) => {
    return Object.keys(filter)
                 .reduce((acc: { [key: string]: string }, key: string) => {
                     if (filter[key] !== '') {
                         acc[key] = filter[key];
                     }
                     return acc;
                 }, {});

};

export const fetchRecords = async (filter?: { [key: string]: string }) => {

    // filter looks like this - {accountId: 'xxxxx', email: 'XXXXXXXXXXXXX'}

    let transformedFilter = {};
    if (typeof filter !== 'undefined') {
        transformedFilter = normalizeFilter(filter);
    }

    // after normalization filter looks like this - {accountId: 'xxxxx', email: 'XXXXXXXXXXXXX'}

    // FNP-2070: follow nextToken to the end. Reading only the first page let an
    // existing record fall off it, and every "is this application enabled?" check
    // then answered no.
    try {
        const items: AccountApplication[] = [];
        let nextToken: string | null | undefined;
        do {
            const response = await client.graphql<GraphQLQuery<ListAccountApplications>>({
                                                                                             query: listAccountApplications,
                                                                                             variables: {
                                                                                                 filter: transformedFilter,
                                                                                                 ...(nextToken ? {nextToken} : {}),
                                                                                             },
                                                                                         });
            const page = response.data?.listAccountApplications;
            items.push(...(page?.items ?? []));
            // A token that does not advance would loop forever.
            nextToken = page?.nextToken === nextToken ? null : page?.nextToken;
        } while (nextToken);
        return {items, nextToken: null};
    } catch (e) {
        console.trace(e);
        notifyError(e)
    }
};

export const fetch = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetAccountApplication>>({
                                                                                       query: getAccountApplication,
                                                                                       variables: {id: id},
                                                                                   });
        return response.data?.getAccountApplication;
    } catch (e) {
        console.trace(e);
        notifyError(e)
    }
};

// FNP-2070: the two typed errors console-api's create raises since FNP-902. Each
// gets a message a portal user can act on instead of the raw resolver text.
export const ALREADY_ACTIVE_MESSAGE = 'This application is already enabled for your account.';
export const PRE_CHECK_INCOMPLETE_MESSAGE =
    'We could not confirm whether this application is already enabled, so nothing was changed. Please try again.';

type GraphQLErrorEntry = { message?: string, errorType?: string, errorInfo?: { id?: string } | null };

const firstGraphQLError = (e: unknown): GraphQLErrorEntry | undefined =>
    (e as { errors?: GraphQLErrorEntry[] })?.errors?.[0];

const accountApplicationErrorMessage = (e: unknown) => {
    switch (firstGraphQLError(e)?.errorType) {
        case 'AccountApplicationAlreadyActive':
            return ALREADY_ACTIVE_MESSAGE;
        case 'AccountApplicationPreCheckIncomplete':
            return PRE_CHECK_INCOMPLETE_MESSAGE;
        default:
            return undefined;
    }
};

const createRecord = async (payload: object) => {
    const response = await client.graphql<GraphQLQuery<CreateAccountApplication>>({
                                                                                      query: createAccountApplication,
                                                                                      variables: {input: payload},
                                                                                  });
    return response.data?.createAccountApplication;
};

export const add = async (payload: object) => {

    try {
        const record = await createRecord(payload);
        notifySuccess();
        return record;
    } catch (e) {
        console.trace(e);
        const message = accountApplicationErrorMessage(e);
        if (message) {
            showErrorNotification(message);
        } else {
            notifyError(e)
        }
    }
};

// FNP-2070: the ONE way the portal turns an application on for an account. Since
// FNP-902 console-api keeps one record per (account, application): create with
// ACTIVE makes it when there is none and reactivates a not-ACTIVE one in place,
// same id. So no caller checks for a record first; a check-then-create raced and
// skipped INACTIVE records, which never reactivated.
//
// An ACTIVE record already is the state asked for, so AccountApplicationAlreadyActive
// resolves with its id. Every other failure rejects, and no toast is shown here:
// the caller decides how loud a failure is.
export const enableAccountApplication = async (
    accountId: string,
    applicationId: string,
): Promise<{ id: string, alreadyActive: boolean }> => {
    try {
        const record = await createRecord({
                                              accountId,
                                              applicationId,
                                              accountApplicationStatus: AccountApplicationStatus.ACTIVE,
                                          });
        return {id: record?.id as string, alreadyActive: false};
    } catch (e) {
        const error = firstGraphQLError(e);
        if (error?.errorType === 'AccountApplicationAlreadyActive') {
            return {id: error.errorInfo?.id as string, alreadyActive: true};
        }
        throw new Error(accountApplicationErrorMessage(e) ?? error?.message ?? (e as Error)?.message ?? 'Could not enable the application.');
    }
};

export const update = async (id: string, record: AccountApplication) => {


    console.debug('IN UPDATE', id, record);

    // FNP-2070: send only what UpdateAccountApplicationInput declares. Callers
    // pass the whole listed record (account, application, timestamps), and AppSync
    // rejects an input carrying a field its type does not declare.
    const input = {
        id: record.id,
        accountId: record.accountId,
        applicationId: record.applicationId,
        accountApplicationStatus: record.accountApplicationStatus,
    };

    try {
        const response = await client.graphql<GraphQLQuery<UpdateAccountApplication>>({
                                                                                          query: (updateAccountApplication),
                                                                                          variables: {input},
                                                                                      });

        notifySuccess('AccountApplication updated');
        return response.data?.updateAccountApplication;
    } catch (e) {
        console.trace(e);
        notifyError(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteAccountApplication>>({
                                                                                          query: (deleteAccountApplication),
                                                                                          variables: {input: {id: id}},

                                                                                      });
        notifySuccess('AccountApplication deleted');
        return response.data?.deleteAccountApplication;
    } catch (e) {
        console.trace(e);
        notifyError(e)
        // FNP-795: RE-THROW. Swallowing here made the thunk resolve, so
        // deleteOneCase's `fulfilled` branch removed the row from the store even
        // though the server had refused — the UI claimed the seat was released while
        // it was still occupied, and only a refresh revealed otherwise.
        //
        // Latent before, load-bearing now: console-api REJECTS deleting an ACTIVE
        // AccountApplication (it is an occupied seat), so this path is reachable in
        // normal use rather than only on an outage. notifyError already showed the
        // user the real message; re-throwing is what keeps the store honest.
        throw e;
    }
};
